import React, { useState, useEffect } from 'react';
import { Mail, Clock, User, MessageSquare, ArrowLeft, Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const Inquiries = () => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const navigate = useNavigate();

  // Admin password - Change this to your own secure password
  const ADMIN_PASSWORD = 'triansh2026';

  useEffect(() => {
    // Check if already authenticated in this session
    const auth = sessionStorage.getItem('inquiries_auth');
    if (auth === 'true') {
      setIsAuthenticated(true);
      fetchInquiries();
    } else {
      setLoading(false);
    }
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      sessionStorage.setItem('inquiries_auth', 'true');
      fetchInquiries();
      toast.success('Access granted!');
    } else {
      toast.error('Incorrect password');
      setPassword('');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('inquiries_auth');
    setInquiries([]);
    toast.success('Logged out successfully');
  };

  const fetchInquiries = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/contact`);
      
      if (response.ok) {
        const data = await response.json();
        setInquiries(data);
      } else {
        setError('Failed to fetch inquiries');
      }
    } catch (err) {
      console.error('Error fetching inquiries:', err);
      setError('Failed to fetch inquiries');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'new': return 'bg-blue-100 text-blue-800';
      case 'contacted': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-black rounded-full flex items-center justify-center">
                <Lock size={32} className="text-white" />
              </div>
            </div>
            
            <h2 className="text-2xl font-bold text-center mb-2">Admin Access</h2>
            <p className="text-gray-600 text-center mb-6">
              Enter password to view customer inquiries
            </p>

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label htmlFor="password" className="label-custom">
                  Password
                </label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-custom"
                  placeholder="Enter admin password"
                  required
                  autoFocus
                />
              </div>

              <button type="submit" className="cta-button w-full">
                Access Inquiries
              </button>

              <button
                type="button"
                onClick={() => navigate('/')}
                className="cta-button-outline w-full"
              >
                Back to Home
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Loading State
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading inquiries...</p>
        </div>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600">{error}</p>
          <button onClick={fetchInquiries} className="mt-4 cta-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Main Inquiries View
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Header */}
        <div className="mb-8 flex justify-between items-start">
          <div>
            <button 
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-gray-600 hover:text-black mb-4"
            >
              <ArrowLeft size={20} />
              Back to Home
            </button>
            <h1 className="text-4xl font-bold text-black mb-2">Customer Inquiries</h1>
            <p className="text-gray-600">
              Total inquiries: <span className="font-semibold">{inquiries.length}</span>
            </p>
          </div>
          
          <button
            onClick={handleLogout}
            className="cta-button-outline"
          >
            Logout
          </button>
        </div>

        {/* Inquiries List */}
        {inquiries.length === 0 ? (
          <div className="bg-white rounded-lg p-12 text-center">
            <MessageSquare size={48} className="mx-auto text-gray-400 mb-4" />
            <h3 className="text-xl font-semibold text-gray-800 mb-2">No inquiries yet</h3>
            <p className="text-gray-600">Customer inquiries will appear here when submitted</p>
          </div>
        ) : (
          <div className="space-y-4">
            {inquiries.map((inquiry) => (
              <div 
                key={inquiry.id} 
                className="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  {/* Left Section */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <User size={20} className="text-gray-400" />
                      <h3 className="text-lg font-semibold text-black">{inquiry.name}</h3>
                      <span className={`service-button text-xs ${getStatusColor(inquiry.status)}`}>
                        {inquiry.status}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 text-gray-600 mb-2">
                      <Mail size={16} />
                      <a href={`mailto:${inquiry.email}`} className="hover:underline">
                        {inquiry.email}
                      </a>
                    </div>

                    <div className="mb-3">
                      <span className="service-button" style={{ background: '#ffe03d' }}>
                        {inquiry.artType}
                      </span>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4 mt-4">
                      <p className="text-gray-800 whitespace-pre-wrap">{inquiry.message}</p>
                    </div>
                  </div>

                  {/* Right Section */}
                  <div className="flex items-center gap-2 text-gray-500 text-sm lg:flex-col lg:items-end">
                    <Clock size={16} />
                    <span>{formatDate(inquiry.timestamp)}</span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="mt-4 pt-4 border-t border-gray-200 flex gap-3">
                  <a 
                    href={`mailto:${inquiry.email}?subject=Re: ${inquiry.artType} Inquiry&body=Hi ${inquiry.name},%0D%0A%0D%0AThank you for your interest in Triansh Arts!%0D%0A%0D%0A`}
                    className="cta-button-outline text-sm"
                  >
                    Reply via Email
                  </a>
                  <a 
                    href={`https://wa.me/?text=Reply to ${inquiry.name} about ${inquiry.artType}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cta-button-outline text-sm"
                  >
                    Share on WhatsApp
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Inquiries;
